import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

export interface UserProfile {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  role?: string;
  is_admin?: boolean;
}

interface UserContextType {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  loginWithGoogle: () => void;
  devLogin: (email: string, displayName?: string, role?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  loginWithGoogle: () => {},
  devLogin: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

const API_BASE = process.env.REACT_APP_API_URL || "";

const CACHED_USER_STORAGE_KEY = "chordmaster_cached_user";

function loadCachedUser(): UserProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHED_USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setCachedUser(user: UserProfile | null): void {
  if (typeof window === "undefined") return;
  try {
    if (user) {
      localStorage.setItem(CACHED_USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(CACHED_USER_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^|;\\s*)(" + name + ")=([^;]*)"));
  return match ? decodeURIComponent(match[3]) : null;
}

export async function fetchCsrfToken(): Promise<string> {
  const existingCookie = getCookie("chordmaster_csrf");
  if (existingCookie) {
    return existingCookie;
  }
  try {
    const res = await fetch(`${API_BASE}/api/auth/csrf`, { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      return data.csrf_token || "";
    }
  } catch {
    // ignore
  }
  return "";
}

export async function fetchCurrentUser(): Promise<UserProfile | null> {
  const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: "include" });
  if (res.status === 401 || res.status === 403) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to fetch user (${res.status})`);
  }
  const data = await res.json();
  if (!data) return null;
  return {
    id: data.id,
    name: data.display_name || (data.email ? data.email.split("@")[0] : "Guitarist"),
    email: data.email,
    avatarUrl: data.avatar_url,
    role: data.role,
    is_admin: data.is_admin,
  };
}

export async function apiLogout(): Promise<void> {
  const csrfToken = getCookie("chordmaster_csrf");
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;

  await fetch(`${API_BASE}/api/auth/logout`, {
    method: "POST",
    headers,
    credentials: "include",
  });
}

export async function apiDevLogin(email: string, displayName?: string, role: string = "user"): Promise<UserProfile> {
  const csrfToken = getCookie("chordmaster_csrf");
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const res = await fetch(`${API_BASE}/api/auth/dev-login`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      email,
      display_name: displayName || email.split("@")[0],
      role,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Dev login failed");
  }

  const data = await res.json();
  return {
    id: data.id,
    name: data.display_name || (data.email ? data.email.split("@")[0] : "Guitarist"),
    email: data.email,
    avatarUrl: data.avatar_url,
    role: data.role,
    is_admin: data.is_admin,
  };
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(() => loadCachedUser());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      setLoading(true);
      const [, currentUser] = await Promise.all([
        fetchCsrfToken().catch(() => null),
        fetchCurrentUser(),
      ]);
      setUser(currentUser);
      setCachedUser(currentUser);
      const authError = new URLSearchParams(window.location.search).get("error");
      setError(authError ? "Google sign-in failed. Please try again." : null);
    } catch (err: any) {
      setUser(null);
      setCachedUser(null);
      setError(err.message || "Could not restore your session. Please try signing in again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();

    const handleUnauthorized = () => {
      setUser(null);
      setCachedUser(null);
    };

    window.addEventListener("chordmaster_auth_unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("chordmaster_auth_unauthorized", handleUnauthorized);
    };
  }, [refreshUser]);

  const loginWithGoogle = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}/api/auth/google/availability`, { credentials: "include" });
      if (!response.ok) throw new Error("Could not start Google sign-in. Please try again.");
      const availability = await response.json();
      if (!availability.configured) throw new Error(availability.message);
      const returnUrl = window.location.pathname + window.location.search;
      window.location.href = `${API_BASE}/api/auth/google/start?return_url=${encodeURIComponent(returnUrl)}`;
    } catch (error: any) { setError(error.message || "Could not start Google sign-in."); }
  }, []);

  const devLogin = useCallback(async (email: string, displayName?: string, role: string = "user") => {
    try {
      setLoading(true);
      setError(null);
      const loggedIn = await apiDevLogin(email, displayName, role);
      setUser(loggedIn);
      setCachedUser(loggedIn);
    } catch (err: any) {
      setError(err.message || "Login failed");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await apiLogout();
      setUser(null);
      setCachedUser(null);
      setError(null);
    } catch (err) {
      console.error("Logout error:", err);
      setUser(null);
      setCachedUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        error,
        isAuthenticated: user !== null,
        loginWithGoogle,
        devLogin,
        logout,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
